import { useCache } from "@/hooks/useCache";
import { ModelProps } from "./Model";
import { useFilterIsShaken, useFilterSpices } from "@/hooks/useFilter";
import { MixMat } from "./Materials/MixMat";
import { StackMat } from "./Materials/StackMat";
import { Box3, DataArrayTexture, DoubleSide, NearestFilter, NoColorSpace, RepeatWrapping, Texture } from "three"; //prettier-ignore
import { useUniforms } from "@/hooks/textures/useUniforms";
import { useDataArrayTexture } from "@/hooks/textures/useTextures";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { TexType } from "@/data/schema";
import { useTexture } from "@react-three/drei";
import { NOISE_LIBRARY, NoiseResolution } from "@/data/noiseManifest";
import { RESOURCES } from "@/data/resources";
import { folder, useControls } from "leva";
import { useDebug } from '@/hooks/useDebug'

export type MaterialProp = {
  uniforms: {
    uActiveCount: { value: number };
    uActiveTextureSlots: { value: number[] };
    uQuantity: { value: number[] };
    uAccQuantities: { value: number };

    uFallback: { value: DataArrayTexture };

    uTextureScale: { value: number[] };
    uNorIntensity: { value: number[] };
    uRoughnessIntensity: { value: number[] };
    uColorIntensity: { value: number[] };
    uAoIntensity: { value: number[] };

  uHighResMapsReady: { value: boolean[] };
  uNoiseMap: { value: Texture | null };
  uNoiseScale: { value: number };
  uNoiseAmp: { value: number };
};
};

export function Spices({ nodes }: Omit<ModelProps, "textures">) {
  const { data } = useCache();
  const [spices] = useFilterSpices();
  const [isShaken] = useFilterIsShaken();

  const fallback = useDataArrayTexture({spices, cache: data, type: TexType.FALLBACK }); //prettier-ignore
  const texture = useDataArrayTexture({spices, cache: data, type: TexType.TEXTURE }); //prettier-ignore
  const normal = useDataArrayTexture({spices, cache: data, type: TexType.NORMAL }); //prettier-ignore
  const uniforms = useUniforms({ texture: texture, normal: normal, fallback: fallback }); //prettier-ignore
  const bbBox = useRef<Box3>(nodes.Spices.geometry.boundingBox ?? new Box3());
  useEffect(() => {
    if (!nodes.Spices.geometry.boundingBox) {
      nodes.Spices.geometry.computeBoundingBox();
    }
    if (nodes.Spices.geometry.boundingBox) {
      bbBox.current = nodes.Spices.geometry.boundingBox;
    }
  }, [nodes.Spices.geometry]);

  const isDebug = useDebug()

  return (
  <>
    <mesh
      castShadow
      name="Spices"
      geometry={nodes.Spices.geometry}
      visible={spices.size ? true : false}
      scale={[0.985, 1.0, 0.985]}
    >
      {isShaken ? <MixMat uniforms={uniforms} /> :  <StackMat uniforms={uniforms} bbBox={bbBox.current} /> }
    </mesh>
    {isDebug && <Suspense><NoiseDebug uniforms={uniforms}/></Suspense>}
  </>
  );
}

const NOISE_RES_OPTIONS = Object.keys(NOISE_LIBRARY) as NoiseResolution[];
type NoiseType = (typeof NOISE_LIBRARY)[NoiseResolution][number];
function NoiseDebug({ uniforms }: { uniforms: MaterialProp["uniforms"] }) {

  const initial = useMemo(() => {
    const parts = RESOURCES.noise.path.split("/");
    const res = parts.at(-2) as NoiseResolution | undefined;
    const file = parts.at(-1)?.replace(/\.png$/i, "");
    if (!res || !(res in NOISE_LIBRARY) || !file) return null;
    const suffix = `-${res}`;
    const type = file.endsWith(suffix) ? file.slice(0, -suffix.length) : file;
    return { res, type };
  }, []);

  const [noiseRes, setNoiseRes] = useState<NoiseResolution>( initial?.res ?? NOISE_RES_OPTIONS[0] ); //prettier-ignore
  const noiseTypeOptions = useMemo(
    () => Array.from(NOISE_LIBRARY[noiseRes] ?? []) as NoiseType[],
    [noiseRes]
  );
  const [noiseType, setNoiseType] = useState<NoiseType>(
    (initial?.type && noiseTypeOptions.includes(initial.type as NoiseType)
      ? (initial.type as NoiseType)
      : noiseTypeOptions[0]) ?? ("Gaussian_01" as NoiseType)
  ); //prettier-ignore

  useEffect(() => {
    if (initial?.res && initial.res !== noiseRes) setNoiseRes(initial.res);
    if (initial?.type && initial.type !== noiseType && noiseTypeOptions.includes(initial.type as NoiseType)) {
      setNoiseType(initial.type as NoiseType);
    }
    if (noiseTypeOptions.length && !noiseTypeOptions.includes(noiseType)) {
      setNoiseType(noiseTypeOptions[0] as NoiseType);
    }
  }, [initial, noiseRes, noiseType, noiseTypeOptions]);

  const noisePath = useMemo( () => `./textures/noise/${noiseRes}/${noiseType}-${noiseRes}.png`, [noiseRes, noiseType]); //prettier-ignore
  const noiseMap = useTexture(noisePath);

  useEffect(() => {
    if (!noiseMap) return;
    noiseMap.minFilter = NearestFilter;
    noiseMap.magFilter = NearestFilter;
    noiseMap.colorSpace = NoColorSpace;
    noiseMap.wrapS = RepeatWrapping;
    noiseMap.wrapT = RepeatWrapping;
    noiseMap.needsUpdate = true;
    uniforms.uNoiseMap.value = noiseMap;
  }, [noiseMap, uniforms]);

  const controls = useControls(
    "Spices", 
    {
    noise: folder({

      noiseScale: { value: uniforms.uNoiseScale.value, min: 0, max: 2, step: 0.01, 
        onChange: (value: number) => {uniforms.uNoiseScale.value = value},
       },
      noiseAmp: { value: uniforms.uNoiseAmp.value, min: 0, max: 1, step: 0.01, 
        onChange: (value: number) => {uniforms.uNoiseAmp.value = value},
       },

      texture: folder({
        show: false, 
        res: {
          value: noiseRes,
          options: NOISE_RES_OPTIONS,
          onChange: (value: NoiseResolution) => setNoiseRes(value),
        },
        type: {
          value: noiseType,
          options: noiseTypeOptions,
          onChange: (value: NoiseType) => setNoiseType(value),
        },
      },     { collapsed: true }),
    },     { collapsed: true }), 
    }, 
    { collapsed: true }, 
    [uniforms, noiseRes, noiseType, noiseTypeOptions]
  );

  if (!controls.show) return null;
  return (
    <mesh scale={2} position={[1.75, 0, 0]}>
      <planeGeometry />
      <meshBasicMaterial map={uniforms.uNoiseMap.value} side={DoubleSide} />
    </mesh>
  );
}
